import React from "react";
import styles from "./style.module.scss";

export type RaceProfileLap = {
  lapNumber: number;
  energyTargetkWh: number;
  attack: boolean;
  tow: boolean;
};

export type RaceProfileData = {
  linearTargetkWh: number;
  totalAttackTimeS: number;
  attackLapDurationS: number | null;
  attackLapCount: number;
  laps: RaceProfileLap[];
};

export type RaceProfileDraft = RaceProfileData & {
  dataFile: string;
  totalRaceEnergykWh: number;
  numberOfLaps: number;
};

type RaceProfileProps = {
  profile: RaceProfileDraft | null;
  loading: boolean;
  error: string | null;
  canGenerate: boolean;
  inputsChanged: boolean;
  onGenerate: () => void;
  onToggle: (
    lapNumber: number,
    field: "attack" | "tow",
    checked: boolean,
  ) => void;
};

const energyFormat = new Intl.NumberFormat("en-GB", {
  maximumSignificantDigits: 6,
});

export default function RaceProfile({
  profile,
  loading,
  error,
  canGenerate,
  inputsChanged,
  onGenerate,
  onToggle,
}: RaceProfileProps) {
  return (
    <section
      className={`${styles.panel} ${styles.raceProfilePanel}`}
      aria-labelledby="race-profile-title"
      aria-busy={loading}
    >
      <div className={styles.panelHeader}>
        <h2 id="race-profile-title">Race Profile</h2>
      </div>

      <button
        type="button"
        className={styles.profileButton}
        onClick={onGenerate}
        disabled={!canGenerate || loading}
      >
        {loading ? "Generating..." : "Generate Profile"}
      </button>

      {error && (
        <div className={styles.errorMessage} role="alert">
          {error}
        </div>
      )}

      {inputsChanged && (
        <p className={styles.profileHint} role="status">
          Data file, energy or lap count has changed. Generate the profile to
          update the table.
        </p>
      )}

      {profile ? (
        <>
          <p className={styles.linearTarget} aria-live="polite">
            Linear target: {energyFormat.format(profile.linearTargetkWh)}{" "}
            kWh/lap
          </p>
          <p className={styles.attackSummary}>
            Attack default:{" "}
            {profile.attackLapCount > 0
              ? `final ${profile.attackLapCount} ${
                  profile.attackLapCount === 1 ? "lap" : "laps"
                }`
              : "0 laps"}{" "}
            · {energyFormat.format(profile.totalAttackTimeS)} s target
            {profile.attackLapDurationS !== null
              ? ` · ${energyFormat.format(profile.attackLapDurationS)} s/lap`
              : ""}
          </p>
          <div
            className={styles.profileTableContainer}
            role="region"
            aria-label="Scrollable race profile laps"
            tabIndex={0}
          >
            <table
              className={styles.profileTable}
              aria-label="Race profile laps"
            >
              <thead>
                <tr>
                  <th scope="col">Lap</th>
                  <th scope="col">Energy target (kWh)</th>
                  <th scope="col">Attack</th>
                  <th scope="col">Tow</th>
                </tr>
              </thead>
              <tbody>
                {profile.laps.map((lap) => (
                  <tr key={lap.lapNumber}>
                    <th scope="row">{lap.lapNumber}</th>
                    <td>{energyFormat.format(lap.energyTargetkWh)}</td>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Attack lap ${lap.lapNumber}`}
                        checked={lap.attack}
                        disabled={loading}
                        onChange={(event) =>
                          onToggle(
                            lap.lapNumber,
                            "attack",
                            event.target.checked,
                          )
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Tow lap ${lap.lapNumber}`}
                        checked={lap.tow}
                        disabled={loading}
                        onChange={(event) =>
                          onToggle(lap.lapNumber, "tow", event.target.checked)
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </section>
  );
}
